import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { NoteRemove, Send2 } from "iconsax-reactjs";
import { X } from "lucide-react";
import { Button } from "../../../../shared/ui";

export type CompleteTransferMethod = "manual" | "direct";

interface TransferMethodOption {
  id: CompleteTransferMethod;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const TRANSFER_METHOD_OPTIONS: TransferMethodOption[] = [
  {
    id: "manual",
    title: "Record transfer manually",
    description: "Upload proof of payment and update the transaction status",
    icon: <NoteRemove size={20} variant="Outline" color="#6F739B" />,
  },
  {
    id: "direct",
    title: "Send money directly",
    description: "Transfer funds to the customer's recipient",
    icon: <Send2 size={20} variant="Outline" color="#6F739B" />,
  },
];

export interface CompleteTransferChoiceModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: (method: CompleteTransferMethod) => void;
}

export const CompleteTransferChoiceModal: React.FC<
  CompleteTransferChoiceModalProps
> = ({ open, onClose, onContinue }) => {
  const [selectedMethod, setSelectedMethod] =
    useState<CompleteTransferMethod>("manual");

  const handleConfirm = () => {
    onContinue(selectedMethod);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs transition-opacity" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-xl focus:outline-hidden">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <Dialog.Title className="text-[18px] font-semibold text-[#101828]">
                Complete transfer
              </Dialog.Title>
              <Dialog.Description className="text-[13px] text-[#667085]">
                How would you like to complete this transfer?
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1 text-[#98A2B3] transition-colors hover:bg-[#F2F4F7] hover:text-[#344054]"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          {/* Options */}
          <div className="mt-6 flex flex-col gap-3">
            {TRANSFER_METHOD_OPTIONS.map((option) => {
              const isSelected = selectedMethod === option.id;
              return (
                <div
                  key={option.id}
                  onClick={() => setSelectedMethod(option.id)}
                  className={`flex cursor-pointer items-center justify-between rounded-xl border p-4 transition-all ${
                    isSelected
                      ? ""
                      : "border-[#EAECF0] bg-white hover:border-[#D0D5DD]"
                  }`}
                >
                  <div className="flex items-center gap-3.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                      {option.icon}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[14px] font-medium text-[#101828]">
                        {option.title}
                      </span>
                      <span className="text-[12px] text-[#667085]">
                        {option.description}
                      </span>
                    </div>
                  </div>

                  {/* Radio Indicator */}
                  <div className="ml-3 shrink-0">
                    <div
                      className={`flex h-4 w-4 items-center justify-center rounded-full border transition-colors ${
                        isSelected
                          ? "border-[#6F00FF] bg-white"
                          : "border-[#D0D5DD] bg-white"
                      }`}
                    >
                      {isSelected && (
                        <div className="h-2 w-2 rounded-full bg-[#6F00FF]" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action button */}
          <div className="mt-8 flex justify-end">
            <Button
              type="button"
              disabled={!selectedMethod}
              onClick={handleConfirm}
              className="h-10 rounded-[8px] bg-[#020618] px-6 text-[14px] font-medium text-white shadow-xs transition-colors hover:bg-[#0f172a] disabled:bg-[#90A1B9] disabled:text-white disabled:cursor-not-allowed"
            >
              Continue
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
